import {useEffect, useMemo, useState} from "react";
import {App as AntdApp, Alert, Input, Modal, Select, Table, Tag, Tooltip} from "antd";
import {Button, ExportButtons, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useAddRankKeywordsMutation,
	useCheckRankNowMutation,
	useDeleteRankKeywordMutation,
	useGetRankHistoryQuery,
	useGetRankKeywordsQuery,
} from "~/reduxs/api/rankApiSlice";
import RankChart from "./RankChart";
import RankSerpModal from "./RankSerpModal";

/** Sparkline nhỏ trong bảng (trục Y đảo — hạng tốt ở trên). */
function Spark({series = []}) {
	const pts = series.filter((d) => Number(d.position) > 0);
	if (pts.length < 2) return <span style={{color: '#d1d5db'}}>—</span>;
	const W = 90, H = 24;
	const pos = pts.map((d) => Number(d.position));
	let mn = Math.min(...pos), mx = Math.max(...pos);
	if (mx === mn) mx += 1;
	const x = (i) => (W * i) / (pts.length - 1);
	const y = (p) => 2 + (H - 4) * (p - mn) / (mx - mn);
	const d = 'M ' + pts.map((p, i) => `${x(i).toFixed(1)},${y(pos[i]).toFixed(1)}`).join(' L ');
	return (
		<svg width={W} height={H} style={{verticalAlign: 'middle'}}>
			<path d={d} fill="none" stroke="#ca8a04" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
		</svg>
	);
}

/** Ô delta vị trí: dương = tăng hạng (mũi tên lên xanh). */
function Delta({value}) {
	if (value === null || value === undefined) return <span style={{color: '#d1d5db'}}>—</span>;
	if (value === 0) return <span style={{color: '#9ca3af'}}>0</span>;
	const up = value > 0;
	return (
		<span style={{color: up ? '#16a34a' : '#dc2626', fontWeight: 600}}>
			<FontAwesomeIcon icon={up ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'} /> {Math.abs(value)}
		</span>
	);
}

/**
 * Tab chính Theo dõi thứ hạng SERP: thêm từ khoá (thiết bị + quốc gia), bảng vị trí thật +
 * thay đổi + sparkline, xem biểu đồ diễn biến & trang SERP (đối thủ + features), kiểm tra
 * ngay, đồng hồ quota. Gói không có lượt SERP → panel khoá.
 */
function RankTable({projectId}) {

	const {notification} = AntdApp.useApp();

	const [pollOn, setPollOn] = useState(false);
	const {data, isLoading, isFetching} = useGetRankKeywordsQuery(projectId, {
		pollingInterval: pollOn ? 8000 : 0,
	});

	const [addKeywords, {isLoading: adding}] = useAddRankKeywordsMutation();
	const [deleteKeyword] = useDeleteRankKeywordMutation();
	const [checkNow, {isLoading: checking}] = useCheckRankNowMutation();

	// Form thêm nhanh (inline, giống filter bar Khám phá — antd thô chấp nhận cho thanh nhập).
	const [kw, setKw] = useState('');
	const [device, setDevice] = useState('desktop');
	const [loc, setLoc] = useState(2704);

	const [chartOf, setChartOf] = useState(null); // tracked cho biểu đồ
	const [serpOf, setSerpOf] = useState(null);    // tracked cho modal SERP

	const items = useMemo(() => data?.items || [], [data]);
	const quota = data?.quota || {};
	const locations = data?.options?.locations || [];

	// Poll khi còn từ khoá CHƯA từng được kiểm (đang chờ tick nền) — "ngoài top 100" (đã kiểm,
	// position null) KHÔNG tính là chờ, nếu không sẽ poll mãi mãi.
	const hasPending = items.some((i) => !i.checked);

	useEffect(() => {
		setPollOn(hasPending && !!data?.available);
	}, [hasPending, data?.available]);

	const add = async () => {
		const list = kw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
		if (!list.length) return;
		try {
			const res = await addKeywords({projectId, keywords: list, device, location_code: loc}).unwrap();
			notification.success({
				message: 'Đã thêm theo dõi',
				description: `${res?.added ?? 0} từ khoá mới${res?.skipped ? ` (${res.skipped} đã theo dõi)` : ''}. Vị trí cập nhật sau ít phút.`,
			});
			setKw('');
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thêm được từ khoá')});
		}
	};

	const remove = (row) => {
		Modal.confirm({
			title: `Bỏ theo dõi "${row.keyword}"?`,
			content: 'Toàn bộ lịch sử vị trí của từ khoá này sẽ bị xoá.',
			okText: 'Bỏ theo dõi', okButtonProps: {danger: true}, cancelText: 'Huỷ',
			onOk: async () => {
				try {
					await deleteKeyword({projectId, trackedId: row.id}).unwrap();
					notification.success({message: 'Đã bỏ theo dõi', description: row.keyword});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xoá được')});
				}
			},
		});
	};

	const doCheck = async () => {
		try {
			await checkNow(projectId).unwrap();
			notification.success({message: 'Đã yêu cầu kiểm tra', description: 'Vị trí sẽ cập nhật sau ít phút.'});
			setPollOn(true);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không kiểm tra được')});
		}
	};

	if (isLoading) return <Loading />;

	// Gói không có lượt SERP (và không phải root) → panel khoá.
	if (data && quota.can === false) {
		return (
			<Alert type="warning" showIcon style={{marginTop: 8}}
				message="Gói của bạn chưa hỗ trợ Theo dõi thứ hạng SERP"
				description={<>
					Theo dõi vị trí thật trên Google (Desktop/Mobile + quốc gia) có ở gói Pro trở lên.
					<div style={{marginTop: 10}}>
						<Button primary small onClick={() => (window.location.href = '/subscription')}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-dots" />}>Xem các gói</Button>
					</div>
				</>} />
		);
	}

	const deviceLabel = (d) => (d === 'mobile' ? 'Mobile' : 'Desktop');

	const columns = [
		{title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true},
		{title: 'Thiết bị', dataIndex: 'device', key: 'device', width: 100, responsive: ['md'],
			render: (d) => <Tag icon={<FontAwesomeIcon icon={d === 'mobile' ? 'fa-light fa-mobile' : 'fa-light fa-desktop'} />}>{deviceLabel(d)}</Tag>},
		{title: 'Quốc gia', dataIndex: 'location', key: 'location', width: 110, responsive: ['lg']},
		{title: 'Vị trí', dataIndex: 'position', key: 'position', width: 90, align: 'center',
			sorter: (a, b) => (a.position ?? 999) - (b.position ?? 999),
			render: (v, r) => {
				if (v !== null && v !== undefined) return <strong style={{fontSize: 15}}>#{v}</strong>;
				// Chưa từng kiểm + hệ thống sẵn sàng → đang chờ tick nền.
				if (!r.checked && data?.available) {
					return <Tooltip title="Đang kiểm tra nền — chờ ít phút"><FontAwesomeIcon icon="fa-light fa-spinner" className="fa-spin" /></Tooltip>;
				}
				// Đã kiểm nhưng không lọt top 100.
				if (r.checked) {
					return <Tooltip title={`Ngoài top 100 (kiểm ${r.checked_date || ''})`}><span style={{color: '#9ca3af'}}>&gt;100</span></Tooltip>;
				}
				return <span style={{color: '#9ca3af'}}>—</span>;
			}},
		{title: 'Thay đổi', dataIndex: 'delta', key: 'delta', width: 90, align: 'center', responsive: ['md'],
			render: (v) => <Delta value={v} />},
		{title: 'Tốt nhất', dataIndex: 'best', key: 'best', width: 90, align: 'center', responsive: ['lg'],
			render: (v) => (v ? `#${v}` : <span style={{color: '#9ca3af'}}>—</span>)},
		{title: 'Diễn biến', key: 'spark', width: 110, align: 'center', responsive: ['md'],
			render: (_, r) => <Spark series={r.series} />},
		{title: '', key: 'action', align: 'right', width: 110,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 6}}>
					<Tooltip title="Biểu đồ diễn biến"><Button small white onClick={() => setChartOf(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-line" />} /></Tooltip>
					<Tooltip title="Xem trang SERP + đối thủ"><Button small white onClick={() => setSerpOf(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-list-ol" />} /></Tooltip>
					<Tooltip title="Bỏ theo dõi"><Button small red onClick={() => remove(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} /></Tooltip>
				</span>
			)},
	];

	const exportRows = () => items.map((r) => [
		r.keyword, deviceLabel(r.device), r.location,
		r.position ?? '', r.delta ?? '', r.best ?? '', r.checked_date ?? '',
	]);
	const exportHead = ['Từ khoá', 'Thiết bị', 'Quốc gia', 'Vị trí', 'Thay đổi', 'Tốt nhất', 'Ngày kiểm tra'];
	const buildExcel = () => [{name: 'Theo dõi thứ hạng', aoa: [exportHead, ...exportRows()]}];
	const buildPdf = () => ({title: 'Theo dõi thứ hạng SERP', sections: [{columns: exportHead, rows: exportRows()}]});

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Vị trí THẬT trên Google (không phải trung bình GSC). Kiểm tra chạy nền — mỗi tổ hợp từ khoá × thiết bị × quốc gia dùng chung 1 lần đo/ngày cho mọi dự án, đối thủ hiện miễn phí từ cùng kết quả." />

			{quota.sandbox && (
				<Alert type="warning" showIcon style={{marginBottom: 14}}
					message="Đang dùng dữ liệu SERP mẫu (chế độ thử nghiệm) — vị trí là số mô phỏng để kiểm thử." />
			)}

			{/* Thanh thêm nhanh */}
			<div className="form" style={{display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12}}>
				<Input style={{width: 260}} placeholder="Từ khoá cần theo dõi (nhiều dòng/dấu phẩy)…"
					value={kw} onChange={(e) => setKw(e.target.value)} onPressEnter={add} allowClear />
				<Select style={{width: 130}} value={device} onChange={setDevice}
					options={[{value: 'desktop', label: 'Desktop'}, {value: 'mobile', label: 'Mobile'}]} />
				<Select style={{width: 150}} value={loc} onChange={setLoc} showSearch optionFilterProp="label"
					options={locations.map((l) => ({value: l.code, label: l.label}))} />
				<Button primary small loading={adding} onClick={add}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>Thêm theo dõi</Button>

				<div style={{marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center'}}>
					{typeof quota.remaining === 'number' && (
						<span style={{fontSize: 12, color: '#6b7280'}}>
							Quota SERP: {quota.used}/{quota.limit} tháng ({quota.remaining} còn lại)
						</span>
					)}
					<Button small white loading={checking || isFetching} onClick={doCheck}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-rotate" />}>Kiểm tra ngay</Button>
					<ExportButtons filename={`thu-hang-${projectId}`} buildExcel={buildExcel} buildPdf={buildPdf} />
				</div>
			</div>

			<Table
				rowKey="id"
				size="small"
				loading={isFetching && !pollOn}
				columns={columns}
				dataSource={items}
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}}
				locale={{emptyText: 'Chưa theo dõi từ khoá nào. Nhập từ khoá phía trên để bắt đầu.'}}
			/>

			{/* Modal biểu đồ diễn biến */}
			<Modal open={!!chartOf} onCancel={() => setChartOf(null)} footer={null} width={780}
				title={chartOf ? `Diễn biến vị trí: "${chartOf.keyword}"` : ''}>
				{chartOf && <ChartBody projectId={projectId} tracked={chartOf} />}
			</Modal>

			<RankSerpModal open={!!serpOf} onClose={() => setSerpOf(null)} projectId={projectId} tracked={serpOf} />
		</div>
	);
}

/** Nội dung modal biểu đồ (tách để chỉ fetch history khi mở). */
function ChartBody({projectId, tracked}) {
	const {data, isFetching} = useGetRankHistoryQuery({projectId, trackedId: tracked.id});
	if (isFetching) return <Loading />;
	return (
		<div>
			<div style={{fontSize: 13, color: '#6b7280', marginBottom: 10}}>
				{tracked.device === 'mobile' ? 'Mobile' : 'Desktop'} · {tracked.location}
			</div>
			<RankChart series={data?.series || []} />
		</div>
	);
}

export default RankTable;
